import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Check, Info, AlertTriangle, ChevronLeft, Loader2, Camera, X, MapPin, Bike, Car, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, Section } from "../../components/app/ui";
import PlaceInput from "../../components/app/PlaceInput";
import { ridesApi, inr, errorMessage, imageApi } from "../../lib/api";
import { paymentsApi, openCheckout } from "../../lib/razorpay";
import { useAuth } from "../../context/AuthContext";

const ACTIVE_KEY = "kt_active_ride";
const SCOOTER_MAX = 20;
const AUTO_MAX = 50;
const CAR_MAX = 150;
const GOODS_MAX_KM = 50;
const SIZES = [
  { id: "small", label: "Small", hint: "Fits a backpack" },
  { id: "medium", label: "Medium", hint: "A carton / box" },
  { id: "large", label: "Large", hint: "Bulky · Autorickshaw or Car" },
];

const GOODS_TERMS = [
  "I will pack all goods properly. Packaging is my responsibility — the driver and platform are not responsible for packaging.",
  "The driver will only pick up and drop the package. Drivers will NOT help with loading or unloading.",
  "Bike/Scooter carries up to 20 kg, Autorickshaw up to 50 kg, and a 4-wheeler Taxi Car up to 150 kg (bulky items). A 5% GST is added to the fare.",
  "Goods delivery is within the city only and covers trips up to 50 km between pickup and drop.",
  "The driver and platform are NOT responsible for any damage to the package or any accident while delivering.",
  "The driver and platform are NOT responsible for the contents of the package.",
];

export default function GoodsDelivery() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pickup, setPickup] = useState(null);
  const [recentPickups, setRecentPickups] = useState([]);
  const [drop, setDrop] = useState(null);
  const [receiverName, setReceiverName] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");
  const [instructions, setInstructions] = useState("");
  const [weight, setWeight] = useState("");
  const [vehicle, setVehicle] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [mode, setMode] = useState("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saveReceiver, setSaveReceiver] = useState(true);
  const [payMethod, setPayMethod] = useState("cash");
  const [size, setSize] = useState("small");
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => { ridesApi.receivers().then(setSaved).catch(() => {}); }, []);

  const w = parseFloat(weight);
  const wValid = !isNaN(w) && w > 0;
  const scooterOk = wValid && w <= SCOOTER_MAX && size !== "large";
  const autoOk = wValid && w <= AUTO_MAX;
  const carOk = wValid && w <= CAR_MAX;
  const minSchedule = new Date(Date.now() + 15 * 60000).toISOString().slice(0, 16);

  useEffect(() => { ridesApi.recentPickups().then(setRecentPickups).catch(() => setRecentPickups([])); }, []);

  useEffect(() => {
    if (!wValid) { setVehicle(null); return; }
    if (w > AUTO_MAX) setVehicle("car");
    else if (w > SCOOTER_MAX || size === "large") setVehicle((v) => (v === "car" ? "car" : "auto"));
    else setVehicle((v) => v || "scooter");
  }, [weight, size]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setEstimate(null);
    if (!pickup || !drop) return;
    let alive = true;
    setEstimating(true);
    ridesApi
      .estimate({ pickup_lat: pickup.lat, pickup_lng: pickup.lng, drop_lat: drop.lat, drop_lng: drop.lng, is_intercity: false, purpose: "goods", weight_kg: wValid ? w : undefined })
      .then((e) => { if (alive) setEstimate(e); })
      .catch((e) => toast.error(errorMessage(e, "Could not estimate fare")))
      .finally(() => alive && setEstimating(false));
    return () => { alive = false; };
  }, [pickup, drop, weight]);

  const fareFor = (vt) => estimate?.options?.find((o) => o.vehicle_type === vt)?.fare;
  const breakdown = vehicle && estimate?.goods_breakdowns?.[vehicle];
  const tooFar = estimate?.distance_km != null && estimate.distance_km > GOODS_MAX_KM;
  const outOfCity = !!estimate?.goods_out_of_city;

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Photo must be under 10 MB"); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const base64 = String(dataUrl).split(",")[1];
      const up = await imageApi.upload(base64, file.type || "image/jpeg");
      setPhotoUrl(imageApi.url(up.image_id));
    } catch (err) { toast.error(errorMessage(err, "Couldn't upload photo")); }
    finally { setUploading(false); }
  };
  const photoRequired = size === "large";
  const canBook = pickup && drop && receiverName.trim() && receiverPhone.trim() && wValid && vehicle && (size !== "large" || vehicle === "auto" || vehicle === "car") && (!photoRequired || !!photoUrl) && accepted && !tooFar && !outOfCity && (mode === "now" || scheduledAt);

  const submit = async () => {
    if (!canBook) {
      if (!pickup || !drop) return toast.error("Set pickup and delivery address");
      if (outOfCity) return toast.error("Goods delivery is available within the city only");
      if (tooFar) return toast.error(`Goods delivery covers trips up to ${GOODS_MAX_KM} km`);
      if (!wValid) return toast.error("Enter a valid package weight");
      if (!receiverName.trim() || !receiverPhone.trim()) return toast.error("Add the receiving person's name and phone");
      if (photoRequired && !photoUrl) return toast.error("A photo of the packed, shipping-ready package is required for Large packages");
      if (!accepted) return toast.error("Please read and accept the goods delivery terms");
      if (mode === "later" && !scheduledAt) return toast.error("Pick when the package should be collected");
      return;
    }
    setBusy(true);
    try {
      const common = {
        pickup_lat: pickup.lat, pickup_lng: pickup.lng, drop_lat: drop.lat, drop_lng: drop.lng,
        pickup_address: pickup.name, drop_address: drop.name,
        vehicle_type: vehicle, purpose: "goods",
        receiver_name: receiverName.trim(), receiver_phone: receiverPhone.trim(),
        delivery_instructions: instructions.trim() || null, weight_kg: w, size, goods_terms_accepted: true,
        package_photo_url: photoUrl || null,
      };
      if (saveReceiver) { ridesApi.addReceiver({ name: receiverName.trim(), phone: receiverPhone.trim(), address: drop.name }).catch(() => {}); }
      if (mode === "later") {
        await ridesApi.schedule({ ...common, scheduled_at: new Date(scheduledAt).toISOString(), notes: instructions.trim() || null });
        toast.success("Goods delivery scheduled! We'll alert partners near pickup time.");
        navigate("/app/home");
        return;
      }
      if (payMethod === "online") {
        const r = await ridesApi.book({ ...common, payment_method: "razorpay" });
        try {
          const order = await paymentsApi.createRideOrder(r.ride_id);
          const res = await openCheckout({ order, amount: fareFor(vehicle), user, description: "Goods delivery (prepaid)" });
          await paymentsApi.verifyRide({ ride_id: r.ride_id, razorpay_order_id: res.razorpay_order_id || order.order_id || `web_${r.ride_id}`, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature || null, method: "online" });
        } catch (pe) {
          toast.error(pe?.cancelled ? "Payment cancelled — your package wasn't booked." : errorMessage(pe, "Payment failed — your package wasn't booked."));
          return;
        }
        localStorage.setItem(ACTIVE_KEY, r.ride_id);
        toast.success("Payment received! Finding a delivery partner for your package…");
        navigate("/app/trip");
        return;
      }
      const r = await ridesApi.book({ ...common, payment_method: "cash" });
      localStorage.setItem(ACTIVE_KEY, r.ride_id);
      toast.success("Finding a delivery partner for your package…");
      navigate("/app/trip");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "goods_out_of_city") {
        toast.error(d.message || "Goods delivery is available within the city only.");
      } else {
        toast.error(errorMessage(e, "Could not book goods delivery"));
      }
    } finally {
      setBusy(false);
    }
  };

  const VehicleOption = ({ vt, label, limit, enabled }) => {
    const fare = fareFor(vt);
    const active = vehicle === vt;
    const VIcon = vt === "scooter" ? Bike : vt === "auto" ? Truck : Car;
    return (
      <button
        type="button" onClick={() => enabled && setVehicle(vt)} disabled={!enabled}
        className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-brand bg-brand-50" : "border-surface-300 bg-white hover:border-brand-200"} ${!enabled ? "opacity-40" : ""}`}
        data-testid={`goods-vehicle-${vt}`}
      >
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${active ? "bg-brand text-white" : "bg-surface-100 text-ink-soft"}`}><VIcon className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-ink">{label}</span>
          <span className="mt-0.5 block text-xs text-ink-muted">{limit}</span>
        </span>
        {estimating ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> : fare != null ? <span className="text-base font-bold text-ink">{inr(fare)}</span> : null}
        {active ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand text-white"><Check className="h-3.5 w-3.5" /></span> : null}
      </button>
    );
  };

  return (
    <div className="mx-auto max-w-2xl pb-16">
      <button type="button" onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm font-semibold text-ink-soft hover:text-ink" data-testid="goods-back">
        <ChevronLeft className="h-4 w-4" /> Back
      </button>
      <PageHeader title="Goods Delivery" description="Send a package across the city by Bike/Scooter or Autorickshaw. You pack it, our partner delivers it." />

      <div className="space-y-6">
        <Section title="Pickup & delivery">
          <div className="space-y-4">
            <PlaceInput label="Pickup address" value={pickup} onChange={setPickup} placeholder="Where to collect the package" allowCurrent testId="goods-pickup" />
            {recentPickups.length > 0 && !pickup && (
              <div className="flex flex-wrap gap-2" data-testid="goods-recent-pickups">
                <span className="w-full text-[11px] font-semibold uppercase tracking-wider text-ink-muted">Recent pickups</span>
                {recentPickups.map((p, i) => (
                  <button key={i} type="button" onClick={() => setPickup({ lat: p.lat, lng: p.lng, name: p.address })} className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-surface-300 bg-white px-3 py-1.5 text-xs font-semibold text-ink-soft hover:border-brand hover:text-brand" data-testid={`goods-recent-pickup-${i}`}>
                    <MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{p.address}</span>
                  </button>
                ))}
              </div>
            )}
            <PlaceInput label="Delivery address" value={drop} near={pickup} onChange={setDrop} placeholder="Where to deliver the package" testId="goods-drop" />
            {estimate?.distance_km != null && (
              <p className={`flex items-center gap-1.5 text-xs font-bold ${tooFar ? "text-red-600" : "text-ink-soft"}`} data-testid="goods-distance">
                <Info className="h-3.5 w-3.5 shrink-0" /> Distance: {estimate.distance_km} km {tooFar ? `· over the ${GOODS_MAX_KM} km limit` : `· within the ${GOODS_MAX_KM} km limit`}
              </p>
            )}
          </div>
        </Section>

        <Section title="Receiver & package">
          <div className="space-y-4">
            {saved.length > 0 && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Saved receivers</label>
                <div className="flex flex-wrap gap-2">
                  {saved.map((r) => (
                    <button key={r.receiver_id} type="button" onClick={() => { setReceiverName(r.name); setReceiverPhone(r.phone); }} className="rounded-full border border-surface-300 bg-white px-3 py-1.5 text-xs font-bold text-ink hover:border-brand" data-testid={`goods-saved-${r.receiver_id}`}>{r.name}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Receiving person's name</label>
              <Input value={receiverName} onChange={(e) => setReceiverName(e.target.value)} placeholder="Full name" className="h-11 bg-white" data-testid="goods-receiver-name" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Receiving person's phone</label>
              <Input value={receiverPhone} onChange={(e) => setReceiverPhone(e.target.value)} placeholder="10-digit mobile number" className="h-11 bg-white" data-testid="goods-receiver-phone" />
            </div>
            <button type="button" onClick={() => setSaveReceiver((s) => !s)} className="flex items-center gap-2.5" data-testid="goods-save-receiver">
              <span className={`flex h-5 w-5 items-center justify-center rounded border-2 ${saveReceiver ? "border-brand bg-brand text-white" : "border-surface-400"}`}>{saveReceiver && <Check className="h-3 w-3" />}</span>
              <span className="text-xs font-semibold text-ink-soft">Save this receiver for next time</span>
            </button>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Package weight (kg)</label>
              <Input value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="e.g. 8" inputMode="decimal" className="h-11 bg-white" data-testid="goods-weight" />
              {wValid && w > AUTO_MAX && <p className="mt-1 text-xs font-semibold text-red-600">Maximum weight is {AUTO_MAX} kg.</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Package size</label>
              <div className="grid grid-cols-3 gap-2" data-testid="goods-size">
                {SIZES.map((s) => (
                  <button key={s.id} type="button" onClick={() => setSize(s.id)} className={`rounded-2xl border p-3 text-left transition-colors ${size === s.id ? "border-brand bg-brand-50" : "border-surface-300 bg-white hover:border-brand-200"}`} data-testid={`goods-size-${s.id}`}>
                    <span className="block text-sm font-bold text-ink">{s.label}</span>
                    <span className="mt-0.5 block text-[11px] leading-tight text-ink-muted">{s.hint}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Package photo {photoRequired ? <span className="text-red-600">(required for Large)</span> : "(optional)"}</label>
              {photoRequired && <p className="mb-1.5 text-[11px] text-ink-soft">Snap the packed, shipping-ready package. We match this against the delivery photo to settle any dispute.</p>}
              {photoUrl ? (
                <div className="relative inline-block">
                  <img src={photoUrl} alt="Package" className="h-24 w-24 rounded-xl border border-surface-300 object-cover" data-testid="goods-photo-preview" />
                  <button type="button" onClick={() => setPhotoUrl("")} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-graphite text-white" data-testid="goods-photo-remove"><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <label className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed bg-white px-3 py-3 text-sm font-semibold hover:border-brand ${photoRequired ? "border-red-300 text-red-600" : "border-surface-400 text-ink-soft"}`} data-testid="goods-photo-add">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {uploading ? "Uploading…" : photoRequired ? "Add the packed-package photo (required)" : "Add a photo so the partner knows the package"}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="goods-photo-input" />
                </label>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Delivery instructions (optional)</label>
              <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} maxLength={300} placeholder="Floor, landmark, handling notes…" className="w-full rounded-xl border border-surface-300 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand" data-testid="goods-instructions" />
            </div>
          </div>
        </Section>

        <Section title="Payment" description="The sender pays in advance. Choose how.">
          <div className="space-y-2">
            {[{ id: "cash", label: "Cash to the driver", hint: "Pay the partner in cash at pickup." }, { id: "online", label: "Pay online now", hint: "Pay by UPI / card. Partner is dispatched after payment." }].map((o) => (
              <button key={o.id} type="button" onClick={() => setPayMethod(o.id)} className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${payMethod === o.id ? "border-brand bg-brand-50" : "border-surface-300 bg-white hover:border-brand-200"}`} data-testid={`goods-pay-${o.id}`}>
                <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-ink">{o.label}</span><span className="mt-0.5 block text-xs text-ink-muted">{o.hint}</span></span>
                {payMethod === o.id && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand text-white"><Check className="h-3.5 w-3.5" /></span>}
              </button>
            ))}
            {mode === "later" && payMethod === "online" && (
              <p className="flex items-start gap-1.5 rounded-xl bg-brand-50 px-3 py-2 text-xs text-brand"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Online payment applies to deliver-now bookings. Scheduled deliveries are paid in cash at pickup.</p>
            )}
          </div>
        </Section>

        <Section title="Choose vehicle" description={`Bike/Scooter up to ${SCOOTER_MAX} kg · Autorickshaw up to ${AUTO_MAX} kg · Car up to ${CAR_MAX} kg`}>
          <div className="space-y-2">
            {outOfCity && <p className="flex items-start gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900" data-testid="goods-out-of-city"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Goods delivery is available within the city only{estimate?.drop_city ? ` — ${estimate.drop_city} is outside the city` : ""}.</p>}
            {tooFar && <p className="flex items-start gap-1.5 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600" data-testid="goods-too-far"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This delivery is about {estimate.distance_km} km. Goods delivery covers trips up to {GOODS_MAX_KM} km.</p>}
            {!wValid && <p className="flex items-start gap-1.5 rounded-xl bg-brand-50 px-3 py-2 text-xs text-brand"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Enter the package weight to see the vehicles you can book.</p>}
            <VehicleOption vt="scooter" label="Bike / Scooter" limit={`Up to ${SCOOTER_MAX} kg`} enabled={scooterOk} />
            <VehicleOption vt="auto" label="Autorickshaw" limit={`Up to ${AUTO_MAX} kg (also carries under ${SCOOTER_MAX} kg)`} enabled={autoOk} />
            <VehicleOption vt="car" label="Taxi Car (4-wheeler)" limit={`Up to ${CAR_MAX} kg · bulky items`} enabled={carOk} />
          </div>
          {breakdown && (
            <div className="mt-3 rounded-2xl border border-surface-300 bg-surface-50 p-4" data-testid="goods-fare-breakdown">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-ink-muted">Fare breakdown · {breakdown.billed_km} km</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-ink-soft">Base fare (first km)</span><span className="font-semibold text-ink">{inr(breakdown.base)}</span></div>
                <div className="flex justify-between"><span className="text-ink-soft">Distance ({breakdown.charged_km} km, tapered)</span><span className="font-semibold text-ink">{inr(breakdown.distance_charge)}</span></div>
                <div className="flex justify-between"><span className="text-ink-soft">Time ({breakdown.duration_min} min)</span><span className="font-semibold text-ink">{inr(breakdown.time_charge)}</span></div>
                {breakdown.gst_amount > 0 && <div className="flex justify-between"><span className="text-ink-soft">GST ({breakdown.gst_pct}%)</span><span className="font-semibold text-ink">{inr(breakdown.gst_amount)}</span></div>}
                <div className="mt-1 flex justify-between border-t border-surface-300 pt-2 text-base"><span className="font-bold text-ink">Estimated total</span><span className="font-bold text-brand">{inr(breakdown.fare)}</span></div>
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">Longer trips are charged less per km: {breakdown.tiers.map((t) => `≤${t.upto_km}km ${t.rate_pct}%`).join(" · ")}</p>
              <p className="mt-2 flex items-start gap-1.5 rounded-xl bg-brand-50 px-3 py-2 text-[11px] font-medium leading-relaxed text-brand" data-testid="goods-estimate-note"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This is an estimated total, collected at pickup. Final charges may increase with traffic or time taken — any remaining balance is added to your next ride.</p>
            </div>
          )}
        </Section>

        <Section title="Please read & accept">
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-800"><AlertTriangle className="h-4 w-4" /> Conditions for goods delivery</div>
            <ul className="space-y-2">
              {GOODS_TERMS.map((t, i) => (
                <li key={i} className="flex gap-2 text-xs leading-relaxed text-ink" data-testid={`goods-term-${i}`}><span className="text-ink-muted">•</span><span>{t}</span></li>
              ))}
            </ul>
            <button type="button" onClick={() => setAccepted((a) => !a)} className="mt-3 flex items-center gap-2.5" data-testid="goods-accept">
              <span className={`flex h-6 w-6 items-center justify-center rounded-md border-2 ${accepted ? "border-brand bg-brand text-white" : "border-surface-400"}`}>{accepted && <Check className="h-3.5 w-3.5" />}</span>
              <span className="text-xs font-bold text-ink">I have read and accept all the conditions above.</span>
            </button>
          </div>
        </Section>

        <Section title="When">
          <div className="space-y-3">
            <div className="flex rounded-xl bg-surface-100 p-1" data-testid="goods-mode-toggles">
              {[{ id: "now", label: "Deliver now" }, { id: "later", label: "Schedule" }].map((m) => (
                <button key={m.id} type="button" onClick={() => setMode(m.id)} className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${mode === m.id ? "bg-white text-brand shadow-sm" : "text-ink-soft"}`} data-testid={`goods-mode-${m.id}`}>{m.label}</button>
              ))}
            </div>
            {mode === "later" && (
              <div data-testid="goods-schedule-field">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Collection date &amp; time</label>
                <Input type="datetime-local" value={scheduledAt} min={minSchedule} onChange={(e) => setScheduledAt(e.target.value)} className="h-11 bg-white" data-testid="goods-schedule-datetime" />
                <p className="mt-1 text-xs text-ink-muted">We'll find a partner close to this time. Book at least 15 minutes ahead.</p>
              </div>
            )}
          </div>
        </Section>

        <Button type="button" onClick={submit} disabled={!canBook || busy} className="w-full rounded-xl" data-testid="goods-book">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Package className="mr-2 h-4 w-4" />}
          {mode === "later" ? "Schedule goods delivery" : `${payMethod === "online" ? "Pay & book" : "Book goods delivery"}${vehicle && fareFor(vehicle) != null ? ` · ${inr(fareFor(vehicle))}` : ""}`}
        </Button>
      </div>
    </div>
  );
}
